// Either-or forks drawn as one choice node.
//
// The data keeps the two skills as separate nodes that exclude each
// other; the server validates them that way. On screen the pair folds
// into a single medallion at their midpoint, the way World of Warcraft
// draws a choice talent: one node, two options, and once you pick, the
// node shows what you picked. Keys of choice nodes are `choice:<a>|<b>`
// with the option keys sorted, so they are stable.

const PREFIX = 'choice:';

export function isChoiceKey(key) {
  return typeof key === 'string' && key.startsWith(PREFIX);
}

export function choiceKeyFor(a, b) {
  return PREFIX + [a, b].sort().join('|');
}

// Returns { display, groups, optionToChoice }:
//   display         the nodes to draw, exclusive pairs replaced by one choice node
//   groups          Map choiceKey -> { key, options: [nodeA, nodeB], x, y, major, type, requires }
//   optionToChoice  Map optionKey -> choiceKey
export function foldChoices(nodes) {
  const groups = new Map();
  const optionToChoice = new Map();
  for (const n of nodes) {
    for (const other of n.excludes || []) {
      const key = choiceKeyFor(n.key, other);
      if (groups.has(key)) continue;
      const b = nodes.find((x) => x.key === other);
      if (!b) continue;
      const [first, second] = [n, b].sort((p, q) => (p.key < q.key ? -1 : 1));
      const requires = [...new Set([...(first.requires || []), ...(second.requires || [])])];
      groups.set(key, {
        key,
        choice: true,
        options: [first, second],
        x: Math.round((first.x + second.x) / 2),
        y: Math.round((first.y + second.y) / 2),
        major: Boolean(first.major || second.major),
        minLevel: Math.max(first.minLevel || 0, second.minLevel || 0) || undefined,
        type: first.type,
        requires,
      });
      optionToChoice.set(first.key, key);
      optionToChoice.set(second.key, key);
    }
  }
  const mapKey = (k) => optionToChoice.get(k) || k;
  const display = [];
  const seen = new Set();
  for (const n of nodes) {
    const ck = optionToChoice.get(n.key);
    if (ck) {
      if (seen.has(ck)) continue;
      seen.add(ck);
      const g = groups.get(ck);
      display.push({ ...g, requires: [...new Set(g.requires.map(mapKey))] });
    } else {
      display.push({ ...n, requires: (n.requires || []).map(mapKey), excludes: undefined });
    }
  }
  return { display, groups, optionToChoice };
}

// Status of a choice node from the statuses of its options.
export function choiceStatus(group, statusOf) {
  const [a, b] = group.options.map((o) => statusOf(o.key));
  if (a === 'purchased' || b === 'purchased') return 'purchased';
  if (a === 'available' || b === 'available') return 'available';
  return 'locked';
}

export function chosenOption(group, statusOf) {
  return group.options.find((o) => statusOf(o.key) === 'purchased') || null;
}
